import React, { useState } from "react";
import { Formik, Form } from "formik";
import { useDispatch, useSelector } from "react-redux";
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  TextField,
  Typography,
  Chip,
  IconButton
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Swal from "sweetalert2";
import FileUpload from "../../assets/FileUpload";
import { sendCustomEmailNewsletter } from "../../action";
import "../../css/styles.css";

const formatUrl = (url) => {
  if (!url) return "";
  const trimmed = url.trim();
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  return `https://${trimmed}`;
};

const EnviarMailSuscriptores = () => {
  const dispatch = useDispatch();
  const [fileAdjunto, setFileAdjunto] = useState(null);

  const requestSendEmailsNewsletter = useSelector(
    (state) => state.app.requestSendEmailsNewsletter
  );
  const errorMessage = useSelector((state) => state.app.errorMessage);

  const fileToBase64 = (file) => {
    if (file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
      });
    }
  };

  const onDrop = (file) => {
    if (file && file.length > 0) {
      const f = file[0];
      fileToBase64(f).then((base64) => {
        setFileAdjunto({
          nombre: f.name,
          base64: base64,
        });
      });
    }
  };

  const handleSubmit = (values, { resetForm }) => {
    const payload = {
      asunto: values.asunto,
      cuerpo: values.cuerpo,
      link: formatUrl(values.link),
      archivoAdjunto: fileAdjunto,
    };

    dispatch(sendCustomEmailNewsletter(payload));
  };

  if (requestSendEmailsNewsletter === "SUCCESS") {
    Swal.fire({
      title: "Correo enviado",
      text: "Se han enviado los correos exitosamente a los suscriptores",
      icon: "success",
    });
  }

  if (requestSendEmailsNewsletter === "ERROR") {
    Swal.fire({
      title: "Error",
      text: errorMessage || "Ha ocurrido un error al enviar el correo",
      icon: "error",
    });
  }

  return (
    <div className="contenido" style={{ minWidth: 0, width: "100%" }}>
      {requestSendEmailsNewsletter === "LOADING" && (
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={true}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}

      <div className="header">
        <h2>Enviar Mail a Suscriptores</h2>
      </div>

      <Box sx={{ mt: 3, mb: 4, width: "100%", maxWidth: 1200 }}>
        <Formik
          initialValues={{
            asunto: "Newsletter Propins",
            cuerpo: "",
            link: "",
          }}
          validate={(values) => {
            const errors = {};
            if (!values.asunto) {
              errors.asunto = "El asunto es requerido";
            }
            if (!values.cuerpo) {
              errors.cuerpo = "El cuerpo del mensaje es requerido";
            }
            return errors;
          }}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, handleChange, handleBlur, handleSubmit }) => (
            <Form onSubmit={handleSubmit}>
              <Grid container spacing={4}>
                {/* Formulario de redacción */}
                <Grid item xs={12} lg={6}>
                  <Paper elevation={3} sx={{ p: 4, borderRadius: "12px", height: "100%" }}>
                    <Typography variant="h6" sx={{ mb: 3, fontWeight: "bold", color: "#333" }}>
                      Redactar Mensaje
                    </Typography>

                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <label className="milabel">Asunto</label>
                        <TextField
                          fullWidth
                          name="asunto"
                          variant="outlined"
                          size="small"
                          value={values.asunto}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.asunto && Boolean(errors.asunto)}
                          helperText={touched.asunto && errors.asunto}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <label className="milabel">Cuerpo del correo / Mensaje</label>
                        <TextField
                          fullWidth
                          multiline
                          rows={6}
                          name="cuerpo"
                          variant="outlined"
                          placeholder="Escriba aquí el contenido del correo..."
                          value={values.cuerpo}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.cuerpo && Boolean(errors.cuerpo)}
                          helperText={touched.cuerpo && errors.cuerpo}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <label className="milabel">Link de artículo (opcional)</label>
                        <TextField
                          fullWidth
                          name="link"
                          variant="outlined"
                          size="small"
                          placeholder="https://..."
                          value={values.link}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                          Adjuntar archivo o documento (opcional)
                        </Typography>
                        <FileUpload
                          key={fileAdjunto ? fileAdjunto.nombre : "empty"}
                          name="archivoAdjunto"
                          acceptedFiles={[
                            "application/pdf",
                            "image/*",
                            "application/msword",
                            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                          ]}
                          onDrop={onDrop}
                          sizeLimit={10000000}
                          maxFiles={1}
                          showFiles={true}
                        />
                        {fileAdjunto && (
                          <Box sx={{ mt: 1.5, p: 1.5, bgcolor: "#eef8f1", border: "1px solid #1cb244", borderRadius: "6px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Typography variant="body2" sx={{ color: "#1cb244", fontWeight: 500 }}>
                              Archivo seleccionado: <strong>{fileAdjunto.nombre}</strong>
                            </Typography>
                            <Button
                              size="small"
                              color="error"
                              onClick={() => setFileAdjunto(null)}
                              sx={{ textTransform: "none", fontWeight: "bold" }}
                            >
                              <DeleteOutlineIcon />
                            </Button>
                          </Box>
                        )}
                      </Grid>
                    </Grid>

                    <Grid container justifyContent="flex-end" sx={{ mt: 4 }}>
                      <Grid item>
                        <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          sx={{
                            backgroundColor: "#1cb244",
                            "&:hover": { backgroundColor: "#179b3a" },
                            px: 4,
                            py: 1.2,
                            fontWeight: "bold",
                            borderRadius: "8px",
                            textTransform: "none"
                          }}
                        >
                          Enviar Mail a Suscriptores
                        </Button>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                {/* Vista Previa del Correo (Estética Preguntas PI) */}
                <Grid item xs={12} lg={6}>
                  <Paper
                    elevation={3}
                    sx={{
                      p: 0,
                      borderRadius: "12px",
                      overflow: "hidden",
                      bgcolor: "#f7f7f7",
                      border: "1px solid #e0e0e0",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column"
                    }}
                  >
                    <Box sx={{ p: 2, bgcolor: "#fff", borderBottom: "1px solid #eee", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: "bold", color: "#666" }}>
                        Vista Previa del Correo
                      </Typography>
                    </Box>

                    <Box sx={{ p: 3, flexGrow: 1, display: "flex", justifyContent: "center", alignItems: "flex-start" }}>
                      <Box sx={{ width: "100%", maxWidth: 640, bgcolor: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", borderRadius: "4px", overflow: "hidden" }}>
                        {/* Top bar */}
                        <Box sx={{ bgcolor: "#ededed", px: 3, py: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <a href="https://www.propins.cl/" target="_blank" rel="noopener noreferrer">
                            <img src="https://propins.cl/favicon.ico" alt="Propins" height="28" />
                          </a>
                          <Typography variant="caption" sx={{ color: "#666", fontSize: "12px" }}>PROPINSSPA</Typography>
                        </Box>

                        {/* Banner verde */}
                        <Box sx={{ bgcolor: "#1cb244", color: "#ffffff", pt: "30px", pb: "45px", px: 2, textAlign: "center" }}>
                          <Typography variant="h5" sx={{ fontWeight: "bold", fontSize: "26px", lineHeight: 1.15 }}>
                            {values.asunto || "Newsletter Propins"}
                          </Typography>
                        </Box>

                        {/* Cuerpo */}
                        <Box sx={{ p: 4 }}>
                          <Box sx={{ bgcolor: "#f5f5f5", borderRadius: "6px", p: 2.5, fontSize: "14px", color: "#333", minHeight: 120, whiteSpace: "pre-line", lineHeight: 1.6, mb: 2 }}>
                            {values.cuerpo || "El mensaje que escribas aparecerá formateado aquí..."}
                          </Box>

                          {values.link && (
                            <Box sx={{ mt: 3, textAlign: "center" }}>
                              <Button
                                component="a"
                                href={formatUrl(values.link)}
                                target="_blank"
                                variant="contained"
                                sx={{
                                  bgcolor: "#1cb244",
                                  "&:hover": { bgcolor: "#179b3a" },
                                  color: "#fff",
                                  fontWeight: "bold",
                                  fontSize: "16px",
                                  px: 3,
                                  py: 1.5,
                                  borderRadius: "6px",
                                  textTransform: "none"
                                }}
                              >
                                Ver más información
                              </Button>
                            </Box>
                          )}

                          {fileAdjunto && (
                            <Box sx={{ mt: 2.5, p: 1.5, bgcolor: "#eef8f1", border: "1px dashed #1cb244", borderRadius: "6px", fontSize: "13px", color: "#1cb244", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              <span>Archivo adjunto: <strong>{fileAdjunto.nombre}</strong></span>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Box>
    </div>
  );
};

export default EnviarMailSuscriptores;
